import React, { memo, useState, useEffect, useCallback } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { Label } from 'Components/Label';
import { PurpleButton } from 'Components/Button';

import formClasses from 'Themes/form/form.module.css';
import classes from './equipmentTypeModal.module.css';

interface Props {
  isOpen: boolean;
  mode: 'add' | 'edit';
  initialName: string;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (name: string) => void;
  onClose: (e: any) => void;
}

// Add or rename a company custom equipment type (name only).
function EquipmentTypeModal({ isOpen, mode, initialName, submitting, submitError, onSubmit, onClose }: Props) {
  const [name, setName] = useState('');

  useEffect(() => {
    if (isOpen) setName(initialName);
  }, [isOpen, initialName]);

  const onSubmitForm = useCallback(
    (e: any) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) return;
      onSubmit(trimmed);
    },
    [name, onSubmit]
  );

  const canSubmit = !!name.trim() && !submitting;

  return (
    <Modal
      id="equipment-type-modal"
      classes={classes}
      title={mode === 'add' ? 'Add Equipment Type' : 'Edit Equipment Type'}
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onClose}
    >
      <form
        className={`${formClasses.formBase} ${classes.container}`}
        noValidate
        action="#"
        data-testid="equipment-type-form"
        onSubmit={onSubmitForm}
      >
        <Label ariaLabel="Type Name" className={formClasses.label} htmlFor="equipmentTypeName">
          Type Name
        </Label>
        <input
          id="equipmentTypeName"
          type="text"
          className="form-control"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
          data-testid="equipment-type-name"
        />

        {submitError && (
          <p className={classes.error} role="alert" data-testid="type-error">
            {submitError}
          </p>
        )}

        <div className={`modal-footer ${classes.modalFooter}`}>
          <PurpleButton type="button" onClick={onClose} className={classes.cancelButton}>
            Cancel
          </PurpleButton>
          <PurpleButton type="submit" disabled={!canSubmit}>
            {mode === 'add' ? 'Create' : 'Save'}
          </PurpleButton>
        </div>
      </form>
    </Modal>
  );
}

const EquipmentTypeModalMemo = memo(EquipmentTypeModal, areEqual);

export { EquipmentTypeModalMemo as EquipmentTypeModal };
